// ── Core loan math ─────────────────────────────────────────────────────────

/** Annual payment on a fully-amortizing loan. */
function annualPayment(principal, annualRate, years) {
  if (principal <= 0) return 0;
  const r = annualRate / 12;
  const n = years * 12;
  if (r === 0) return (principal / n) * 12;
  const monthly = (principal * (r * Math.pow(1 + r, n))) / (Math.pow(1 + r, n) - 1);
  return monthly * 12;
}

// ── Main calculation ───────────────────────────────────────────────────────

export function calculateDeal(inputs) {
  const { taxCreditPrice, hardDebtRate, loanTerm, subAllocationPct } = inputs;

  const aNoteRate = hardDebtRate / 100;
  const bNoteRate = 0.01;
  const discountRate = 0.05;

  const creditFactor = 0.39 * taxCreditPrice;

  let qei;
  let ownerEquity;
  let hardDebt;
  let leverageLoan;

  if (inputs.qeiDirect && inputs.qeiDirect > 0) {
    qei = inputs.qeiDirect;
    leverageLoan = creditFactor < 1 ? qei * (1 - creditFactor) : 0;
    ownerEquity = Math.min(inputs.ownerEquity, leverageLoan);
    hardDebt = Math.max(0, leverageLoan - ownerEquity);
  } else {
    ownerEquity = inputs.ownerEquity;
    hardDebt = inputs.hardDebt;
    leverageLoan = ownerEquity + hardDebt;
    qei = creditFactor < 1 ? leverageLoan / (1 - creditFactor) : leverageLoan;
  }

  const nmtcEquity = qei * creditFactor;

  const aNote = leverageLoan;
  const bNote = nmtcEquity;

  // Tax credit schedule: 5% × 3 yrs + 6% × 4 yrs = 39%
  const totalTaxCredits = qei * 0.39;
  let cumulative = 0;
  const creditSchedule = Array.from({ length: 7 }, (_, i) => {
    const credits = i < 3 ? qei * 0.05 : qei * 0.06;
    cumulative += credits;
    return { year: i + 1, rate: i < 3 ? '5%' : '6%', credits, cumulative };
  });

  // NMTC debt service — interest-only on both notes
  const annualANote = aNote * aNoteRate;
  const annualBNote = bNote * bNoteRate;
  const totalAnnualNMTCService = annualANote + annualBNote;
  const totalNMTCService7Years = totalAnnualNMTCService * 7;

  // Conventional hard debt — fully amortizing (for comparison)
  const hardDebtAmortizingAnnual  = annualPayment(hardDebt, aNoteRate, loanTerm);
  const hardDebtAmortizingMonthly = hardDebtAmortizingAnnual / 12;

  // ── Fees: Closing (paid at Year 0) ───────────────────────────────────────

  const subAllocationFee   = qei * (subAllocationPct / 100);
  const nmtcConsultantFee  = qei * 0.01;
  const attorneyFees       = Math.min(75000 + (qei / 10000000) * 50000, 300000);
  const accountantFees     = Math.min(20000 + (qei / 10000000) * 30000, 100000);
  const titleInsurance     = Math.max(leverageLoan * 0.0015, 5000);
  const performanceBondFees = leverageLoan * 0.0075;

  const totalClosingFees =
    subAllocationFee +
    nmtcConsultantFee +
    attorneyFees +
    accountantFees +
    titleInsurance +
    performanceBondFees;

  // ── Fees: Annual ──────────────────────────────────────────────────────────

  const assetMgmtFeeAnnual = qei * 0.005;
  const assetMgmtFeeTotal  = assetMgmtFeeAnnual * 7;

  const npvFactor        = (1 - Math.pow(1 + discountRate, -7)) / discountRate;
  const npvAssetMgmtFees = assetMgmtFeeAnnual * npvFactor;

  const totalFeesBurden      = totalClosingFees + assetMgmtFeeTotal;
  const feesEstimate         = totalClosingFees;
  const netProceedsToProject = qei - totalClosingFees;

  // ── 7-year debt service schedule ─────────────────────────────────────────

  let cumulativeNMTCPayment = 0;
  const debtServiceSchedule = Array.from({ length: 7 }, (_, i) => {
    cumulativeNMTCPayment += totalAnnualNMTCService;
    return {
      year: i + 1,
      aNotePayment: annualANote,
      bNotePayment: annualBNote,
      totalNMTCPayment: totalAnnualNMTCService,
      assetMgmtFee: assetMgmtFeeAnnual,
      totalAnnualCost: totalAnnualNMTCService + assetMgmtFeeAnnual,
      hardDebtAmortizing: hardDebtAmortizingAnnual,
      cumulativeNMTCPayment,
    };
  });

  // ── NPV subsidy ───────────────────────────────────────────────────────────

  const npvBNoteForgiveness = bNote / Math.pow(1 + discountRate, 7);
  const netSubsidyNPV = npvBNoteForgiveness - totalClosingFees - npvAssetMgmtFees;
  const subsidyPct    = qei > 0 ? netSubsidyNPV / qei : 0;

  const effectiveRate  = qei > 0 ? totalAnnualNMTCService / qei : 0;
  const leveragePct    = qei > 0 ? (leverageLoan / qei) * 100 : 0;
  const equityPct      = qei > 0 ? (nmtcEquity   / qei) * 100 : 0;
  const ownerEquityPct = qei > 0 ? (ownerEquity  / qei) * 100 : 0;
  const hardDebtPct    = qei > 0 ? (hardDebt     / qei) * 100 : 0;

  const unwindCostEstimate = Math.min(Math.max(qei * 0.003, 15000), 40000);
  const codIncomeEstimate  = bNote;
  const codTaxEstimate     = bNote * 0.30;

  // ── With vs. Without NMTC comparison ───────────────────────────────────────
  // "Without NMTC" = conventional fully-amortizing loan for full QEI at hard debt rate
  const convFullQEIAnnual  = annualPayment(qei, aNoteRate, loanTerm);
  const convFullQEI7Years  = convFullQEIAnnual * 7;
  // "With NMTC" total 7-year cost = NMTC debt service + asset mgmt + closing fees
  const nmtcTotal7YearCost = totalNMTCService7Years + assetMgmtFeeTotal + totalClosingFees;
  // Savings = conventional 7-year cost - NMTC 7-year cost + B-Note forgiven
  const nmtcSavings7Year   = convFullQEI7Years - nmtcTotal7YearCost + bNote;

  return {
    ownerEquity, hardDebt, leverageLoan, nmtcEquity, qei,
    aNote, bNote, aNoteRate, bNoteRate,
    totalTaxCredits, taxCreditPrice, creditSchedule,
    annualANote, annualBNote, totalAnnualNMTCService, totalNMTCService7Years,
    hardDebtAmortizingAnnual, hardDebtAmortizingMonthly,
    debtServiceSchedule,
    subAllocationFee, nmtcConsultantFee, attorneyFees, accountantFees,
    titleInsurance, performanceBondFees, totalClosingFees,
    assetMgmtFeeAnnual, assetMgmtFeeTotal, npvAssetMgmtFees,
    totalFeesBurden, feesEstimate, netProceedsToProject,
    npvBNoteForgiveness, netSubsidyNPV, subsidyPct,
    effectiveRate,
    leveragePct, equityPct, ownerEquityPct, hardDebtPct,
    unwindCostEstimate, codIncomeEstimate, codTaxEstimate,
    convFullQEIAnnual, convFullQEI7Years, nmtcTotal7YearCost, nmtcSavings7Year,
  };
}

// ── Formatting utilities ───────────────────────────────────────────────────

export function fmt(n) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(n);
}

export function fmtPct(n) {
  return `${(n * 100).toFixed(1)}%`;
}

export function fmtRatePct(n) {
  return `${(n * 100).toFixed(2)}%`;
}
